import {
  DiagnosisTypeCode,
  EncounterClass,
  EncounterDischargeReasonCode,
  EncounterInvalidateReasonCode,
  EncounterPausedReasonCode,
  EncounterService,
  LocationAcuityCode,
} from '@quromedical/fhir-common';
import type { EnforceExtends } from '@quromedical/utils';
import type fhir4 from 'fhir/r4';
import { z } from 'zod';
import { AllergyAdmissionCreateRequestSchema } from '../allergy/schema';
import { ConsumableUsageSchema } from '../consumable/schema';
import {
  BaseUUIDSearchSchema,
  DisplayReferenceSchema,
  UUIDSchema,
} from '../domain/models';
import { PaginationSchema } from '../domain/pagination';
import {
  PaginatedBundleSchema,
  PartialPeriodSchema,
  PeriodSchema,
  QuantitySchema,
  RequiredCodingSchema,
} from '../domain/schema';
import { MediaDataSchema } from '../media/base';
import { PartialMedicationRequestWithIdSchema } from '../medication/schema';
import { ObservationPregnancySchema } from '../observation/schema';
import {
  GeneralPractitionerUpdateRequestSchema,
  PatientSummarySchema,
} from '../patient/schema';
import type { WithId } from '../types';
import { LiveDataMeasuresSchema } from '../vitals/schema';
import { ECGSinusRhythmAlertSchema } from '../websocket/schema';

/**
 * Only allows updating of the props that are directly stored on the Admission's Encounter resource
 *
 * For more detailed information refer to the `Admission` interface
 */

const encounterStatus = [
  'planned',
  'arrived',
  'triaged',
  'in-progress',
  'onleave',
  'finished',
  'cancelled',
  'entered-in-error',
  'unknown',
] as const;

export const EncounterDischargeStatusReason = z.nativeEnum(
  EncounterDischargeReasonCode,
);
const encounterPausedStatusReason = z.nativeEnum(EncounterPausedReasonCode);
export const EncounterInvalidateStatusReason = z.nativeEnum(
  EncounterInvalidateReasonCode,
);

export const encounterStatusReasonSchema = z.union([
  EncounterDischargeStatusReason,
  encounterPausedStatusReason,
  EncounterInvalidateStatusReason,
]);
export const EncounterPausedStatusReason = z.nativeEnum(
  EncounterPausedReasonCode,
);

export const EncounterStatusReasonSchema = z.union([
  EncounterDischargeStatusReason,
  EncounterInvalidateStatusReason,
  EncounterPausedStatusReason,
]);

export const AdmissionDiagnosisSchema = z.object({
  condition: DisplayReferenceSchema,
  type: z.nativeEnum(DiagnosisTypeCode).optional(),
});

export const CreateAdmissionDiagnosisSchema = z.object({
  condition: z.string().optional(),
  type: z.nativeEnum(DiagnosisTypeCode).optional(),
});

export type AdmissionDiagnoses = z.infer<typeof AdmissionDiagnosisSchema>;
export type CreateAdmissionDiagnosis = z.infer<
  typeof CreateAdmissionDiagnosisSchema
>;

export const AdmissionSubjectSchema = PatientSummarySchema.extend({
  id: UUIDSchema,
});

export const CoverageIdentifiersSchema = z.object({
  schemeCode: z.string(),
  dependantCode: z.string(),
  subscriberId: z.string(),
});

export const BaseAdmissionMedicalAidAuthorizationSchema = z.object({
  date: z.string().optional(),
  code: z.string().optional(),
});

export const AdmissionMedicalAidAuthorizationSchema =
  BaseAdmissionMedicalAidAuthorizationSchema.extend({
    coverage: CoverageIdentifiersSchema,
  });
export type AdmissionMedicalAidAuthorization = z.infer<
  typeof AdmissionMedicalAidAuthorizationSchema
>;

export const AdmissionStatusHistorySchema = z.object({
  period: PartialPeriodSchema,
  status: z.enum(encounterStatus),
});

export const AdmissionLocationSchema = z.object({
  reference: DisplayReferenceSchema,
  acuity: z.nativeEnum(LocationAcuityCode).optional(),
});

export const BaseAdmissionSchema = z.object({
  status: z.enum(encounterStatus),
  statusReason: EncounterStatusReasonSchema.optional(),
  class: z.nativeEnum(EncounterClass),
  service: z.nativeEnum(EncounterService),
  acute: RequiredCodingSchema.array().optional(),
  chronic: RequiredCodingSchema.array().optional(),
  reasonOfAdmission: RequiredCodingSchema.array().optional(),
  period: PeriodSchema,
  /**
   * @deprecated use subject field instead
   */
  patient: DisplayReferenceSchema.describe('This field has been deprecated'),
  subject: AdmissionSubjectSchema,
  oxygen: QuantitySchema.optional(),
  statusHistory: AdmissionStatusHistorySchema.array().optional(),
  diagnosis: AdmissionDiagnosisSchema.array().optional(),
  pregnancy: ObservationPregnancySchema.optional(),
  location: DisplayReferenceSchema.optional(),
  admissionLocation: AdmissionLocationSchema.optional(),
});

export const MedicalAidAdmissionSchema = BaseAdmissionSchema.extend({
  primaryIcd10Code: RequiredCodingSchema.optional(),
  medicalAidAuthorization: AdmissionMedicalAidAuthorizationSchema,
});

export const AdmissionSchema = BaseAdmissionSchema.extend({
  icd10Codes: RequiredCodingSchema.array().optional(),
  medicalAidAuthorization: AdmissionMedicalAidAuthorizationSchema.optional(),
  primaryIcd10Code: RequiredCodingSchema,
});

export type Admission = z.infer<typeof AdmissionSchema>;

export const AdmissionWithUsagesSchema = AdmissionSchema.extend({
  medicationUsages: PartialMedicationRequestWithIdSchema.array(),
  consumableUsages: ConsumableUsageSchema.partial().array(),
});
/**
 * This type is meant to be able to build a admission summary card. This is still a TODO item
 * on the front-end.
 */
export type AdmissionWithUsages = z.infer<typeof AdmissionWithUsagesSchema>;

export const AdmissionVitalSummarySchema = z.object({
  vitals: LiveDataMeasuresSchema,
  alerts: ECGSinusRhythmAlertSchema,
});

export const AdmissionMediaSchema = z.object({
  media: DisplayReferenceSchema.array().optional(),
});
export type AdmissionMedia = z.infer<typeof AdmissionMediaSchema>;

export const AdmissionGetResponseSchema = AdmissionSchema.partial().extend({
  id: UUIDSchema,
  vitals: LiveDataMeasuresSchema.optional(),
  alerts: ECGSinusRhythmAlertSchema.array().optional(),
});

export type AdmissionGetResponse = z.infer<typeof AdmissionGetResponseSchema>;

export const AdmissionGetResponseWithMediaSchema =
  AdmissionGetResponseSchema.and(AdmissionMediaSchema);

export type AdmissionGetResponseWithMedia = z.infer<
  typeof AdmissionGetResponseWithMediaSchema
>;

const AdmissionGetResponseWithVitalsSchema = AdmissionGetResponseSchema.extend({
  vitals: LiveDataMeasuresSchema.optional(),
  alerts: ECGSinusRhythmAlertSchema.array().optional(),
  media: DisplayReferenceSchema.array().optional(),
});
export type AdmissionGetResponseWithVitals = z.infer<
  typeof AdmissionGetResponseWithVitalsSchema
>;

export const AdmissionGetAllResponseSchema = PaginatedBundleSchema.extend({
  admissions: AdmissionGetResponseSchema.array(),
});

export type AdmissionGetAllResponse = z.infer<
  typeof AdmissionGetAllResponseSchema
>;

export interface AdmissionGetAllResponseWithMedia {
  admissions: AdmissionGetResponseWithMedia[];
}

type SupportedEncounterStatus = (typeof encounterStatus)[number];

const AdmissionStatusSchema = z
  .enum(encounterStatus)
  .describe('The status of the admission');

const CommaSeparatedAdmissionStatusSchema = z.string().refine(
  (val) => {
    const statuses = val
      .split(',')
      .map((s) => s.trim() as SupportedEncounterStatus);
    return statuses.every((s) => AdmissionStatusSchema.options.includes(s));
  },
  {
    message: 'One or more statuses are invalid',
  },
);

const AdmissionClassSchema = z
  .nativeEnum(EncounterClass)
  .describe('The class of the service');
const AdmissionServiceTypeSchema = z
  .nativeEnum(EncounterService)
  .describe('The service type for the patient');

export const AdmissionSearchSchema = PaginationSchema.extend({
  status: CommaSeparatedAdmissionStatusSchema.optional(),
  class: AdmissionClassSchema.optional(),
  serviceType: AdmissionServiceTypeSchema.optional(),
  patient: BaseUUIDSearchSchema.optional().describe('The patient ids'),
  patientName: z.string().optional().describe('The name of the patient'),
  sort: z.string().optional().describe('The field to sort by'),
  includeAdminAssignments: z
    .boolean()
    .optional()
    .describe('Should include admin assignments'),
  includeVitals: z.boolean().optional().describe('Should include vitals'),
  location: BaseUUIDSearchSchema.optional().describe(
    'The location of the admission',
  ),
});

export type AdmissionSearch = z.infer<typeof AdmissionSearchSchema>;
export type AdmissionGetParams = AdmissionSearch;
export type AdmissionStatus = AdmissionSearch['status'];
export type AdmissionGetAllParams = AdmissionSearch;

export const AdmissionWithoutIcd10PrimaryCode = AdmissionSchema.omit({
  primaryIcd10Code: true,
});

export const AdmissionWithPartialPrimaryCodeSchema =
  AdmissionWithoutIcd10PrimaryCode.extend({
    primaryIcd10Code: RequiredCodingSchema.optional(),
  });
export type AdmissionWithPartialPrimaryCode = z.infer<
  typeof AdmissionWithPartialPrimaryCodeSchema
>;

export const AdmissionGetOneByIdResponseSchema =
  AdmissionWithPartialPrimaryCodeSchema.partial().extend({
    id: UUIDSchema,
  });
export type AdmissionGetOneByIdResponse = z.infer<
  typeof AdmissionGetOneByIdResponseSchema
>;

export type AdmissionUpdateFields = Pick<
  AdmissionWithPartialPrimaryCode,
  | 'primaryIcd10Code'
  | 'status'
  | 'icd10Codes'
  | 'oxygen'
  | 'period'
  | 'medicalAidAuthorization'
  | 'service'
  | 'diagnosis'
  | 'location'
>;

export const ICD10CodeSchema = z.string();

export const CreateAdmissionSchema = z.object({
  class: AdmissionClassSchema,
  service: AdmissionServiceTypeSchema,
  periodStart: z.string(),
  practitioners: GeneralPractitionerUpdateRequestSchema,
  primaryIcd10Code: ICD10CodeSchema.optional(),
  icd10Codes: ICD10CodeSchema.array().optional(),
  acute: ICD10CodeSchema.array().optional(),
  chronic: ICD10CodeSchema.array().optional(),
  reasonOfAdmission: ICD10CodeSchema.array().min(1),
  medicalAidAuthorization:
    BaseAdmissionMedicalAidAuthorizationSchema.optional(),
  oxygen: z.number().optional(),
  attachments: MediaDataSchema.array().optional(),
  diagnosis: CreateAdmissionDiagnosisSchema.array().optional(),
  pregnancy: ObservationPregnancySchema.optional(),
  location: UUIDSchema,
  allergy: AllergyAdmissionCreateRequestSchema.optional(),
});

export const CreateAdmissionSchemaWithPatientSchema =
  CreateAdmissionSchema.extend({
    patientId: UUIDSchema,
  });

/**
 * For more detailed information refer to the `Admission` interface
 */

export type CreateAdmissionInput = z.infer<typeof CreateAdmissionSchema>;
export type CreateAdmissionSchemaWithPatientInput = z.infer<
  typeof CreateAdmissionSchemaWithPatientSchema
>;
export type AdmissionCreateRequest = CreateAdmissionInput;

export const AdmissionUpdateSchema = z.object({
  id: UUIDSchema,
  primaryIcd10Code: ICD10CodeSchema.optional(),
  icd10Codes: ICD10CodeSchema.array().optional(),
  oxygen: z.number().optional(),
  medicalAidAuthorization:
    BaseAdmissionMedicalAidAuthorizationSchema.optional(),
  periodStart: z.string(),
  attachments: MediaDataSchema.array().optional(),
  service: AdmissionServiceTypeSchema,
  diagnosis: CreateAdmissionDiagnosisSchema.array().optional(),
  pregnancy: ObservationPregnancySchema.optional(),
  location: UUIDSchema.optional(),
  reasonOfAdmission: ICD10CodeSchema.array().optional(),
});

export type AdmissionUpdateInput = z.infer<typeof AdmissionUpdateSchema>;
export type AdmissionUpdateRequest = AdmissionUpdateInput;

export const AdmissionLocationUpdateSchema = z.object({
  location: z.string(),
});

export type AdmissionLocationUpdateRequest = z.infer<
  typeof AdmissionLocationUpdateSchema
>;

export type AdmissionCreateResponse = WithId<Partial<Admission>>;
export type AdmissionUpdateResponse = WithId<Partial<Admission>>;
export type BaseAdmission = z.infer<typeof BaseAdmissionSchema>;
export type AdmissionLocation = z.infer<typeof AdmissionLocationSchema>;
export type MedicalAidAdmission = z.infer<typeof MedicalAidAdmissionSchema>;
export type AdmissionDiagnosis = z.infer<typeof AdmissionDiagnosisSchema>;

type FhirEncounterStatus = fhir4.Encounter['status'];
export type EncounterStatus = EnforceExtends<
  FhirEncounterStatus,
  SupportedEncounterStatus
>;

export interface AdmissionCreateMedicalAidAuthorization {
  date?: string;
  code?: string;
}

export const AdmissionDischargeRequestSchema = z.object({
  id: UUIDSchema,
  periodEnd: z.string().date(),
  statusReason: z.nativeEnum(EncounterDischargeReasonCode),
});
export type AdmissionDischargeRequest = z.infer<
  typeof AdmissionDischargeRequestSchema
>;

export const AdmissionStartRequestSchema = z.object({
  id: UUIDSchema,
  periodStart: z.string().date(),
});
export type AdmissionStartRequest = z.infer<typeof AdmissionStartRequestSchema>;

export const AdmissionPauseRequestSchema = z.object({
  id: UUIDSchema,
  statusReason: z.nativeEnum(EncounterPausedReasonCode),
});
export type AdmissionPauseRequest = z.infer<typeof AdmissionPauseRequestSchema>;

export const AdmissionInvalidateRequestSchema = z.object({
  id: UUIDSchema,
  statusReason: z.nativeEnum(EncounterInvalidateReasonCode),
});
export type AdmissionInvalidateRequest = z.infer<
  typeof AdmissionInvalidateRequestSchema
>;
